/**
 * What to say beside a cashflow shortfall in aged care (owner, 6 Oct 2026).
 *
 * A large negative number reads as alarming, and usually it is not: care tends to cost more than the
 * pension and other income pay, and most people meet the difference from savings. What matters is
 * whether the savings last. So the reassurance is only given when they do — measured against the
 * time the horizon rule plans the person's care on (their age and health, strategyHorizon.ts), the
 * same assumption the advice itself rests on. When they may not, it says so, and points to step 3.
 */
import { horizonYearsForPerson } from '../../../../shared/src/calculation/strategyHorizon.ts';
import { summariseFunding, type FundingProjectionYear } from '../../components/insights/fundingSummary.tsx';
import { calculateAge } from '../../utils/index.ts';

export interface RunwayFacts {
    /** The year the savings run out, or null if they last the whole projection. */
    runwayYears: number | null;
    /** How many years the projection covers. */
    projectedYears: number;
}

/** How long the savings last on a projection — the same reading as "How long the money lasts". */
export function runwayFactsOf(projections: unknown): RunwayFacts | null {
    const s = summariseFunding(projections as FundingProjectionYear[]);
    if (!s.hasData) return null;
    const runwayYears = s.hasDeficit ? (s.borrowingStartsYear ?? s.moneyRunsOutYear ?? null) : null;
    return { runwayYears, projectedYears: s.totalYears ?? 0 };
}

interface CareFactFind {
    adviceFor?: string;
    goals?: unknown;
    client?: { firstName?: string; dob?: string; healthStatus?: unknown; sex?: unknown };
    partner?: { firstName?: string; dob?: string; healthStatus?: unknown; sex?: unknown };
}

/** The years care is planned for, and whose: the person entering care, or both. */
export function careYearsFor(ff: CareFactFind): { years: number; name: string } | null {
    const forWhom = (ff?.goals as { aged_care_for?: string } | undefined)?.aged_care_for;
    const couple = ff?.adviceFor === 'couple';
    const c = ff?.client || {};
    const p = ff?.partner || {};
    if (couple && forWhom === 'both') {
        const years = horizonYearsForPerson({
            age: calculateAge(c.dob), partnerAge: calculateAge(p.dob), sex: c.sex, partnerSex: p.sex,
            healthStatus: String(c.healthStatus) === 'poor' || String(p.healthStatus) === 'poor' ? 'poor' : undefined,
        });
        const name = [c.firstName, p.firstName].filter(Boolean).join(' and ') || 'them';
        return years == null ? null : { years, name };
    }
    const person = couple && forWhom === 'partner' ? p : c;
    const years = horizonYearsForPerson({ age: calculateAge(person.dob), healthStatus: person.healthStatus, sex: person.sex });
    return years == null ? null : { years, name: person.firstName || (person === p ? 'your partner' : 'you') };
}

export interface ShortfallOutlook {
    /** Opens the explanation: the shortfall is normal. Plain text. */
    lead: string;
    /** Closes it: whether the savings last long enough. Plain text; empty when it cannot be judged. */
    verdict: string;
    /** true = long enough, false = may not be, null = cannot tell. */
    covered: boolean | null;
}

const yrs = (n: number) => `${n} ${n === 1 ? 'year' : 'years'}`;
const basis = (care: { years: number; name: string }) =>
    `we plan for about ${yrs(care.years)} of care, based on ${care.name === 'you' ? 'your' : `${care.name}'s`} age and health`;

/**
 * `yearsAlreadySaid`: the text beside it has just stated how many years the savings last (the walk-in
 * note does), so the verdict says "that" rather than repeating the figure.
 */
export function shortfallOutlook(runway: RunwayFacts | null, care: { years: number; name: string } | null, opts: { yearsAlreadySaid?: boolean } = {}): ShortfallOutlook {
    const normal = 'A gap like this is normal in aged care: care usually costs more than the pension and other income pays, so most people use their savings to cover the difference.';
    if (!runway || !care) return { lead: normal, verdict: 'What matters is that the savings last.', covered: null };
    if (runway.runwayYears != null) {
        if (runway.runwayYears >= care.years) {
            return { lead: normal, verdict: opts.yearsAlreadySaid ? `That is long enough: ${basis(care)}.` : `The savings last about ${yrs(runway.runwayYears)}, which is long enough: ${basis(care)}.`, covered: true };
        }
        return {
            lead: 'A gap like this is common in aged care, and savings usually cover it.',
            verdict: `${opts.yearsAlreadySaid ? 'That' : `Here they would last about ${yrs(runway.runwayYears)}, which`} may not be long enough: ${basis(care)}. Step 3 shows ways to make the savings go further.`,
            covered: false,
        };
    }
    if (runway.projectedYears >= care.years) {
        return { lead: normal, verdict: `The savings are still there after the ${yrs(runway.projectedYears)} projected, which is long enough: ${basis(care)}.`, covered: true };
    }
    return { lead: normal, verdict: `The savings last at least the ${yrs(runway.projectedYears)} projected. What matters is that they last.`, covered: null };
}
